import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertCircle,
  CheckCircle,
  Eye,
  Globe,
  KeyRound,
  Loader2,
  Pencil,
  Search,
  Send,
  Shield,
  Trash2,
  Users as UsersIcon,
  X,
  XCircle,
} from 'lucide-react';
import { useDeleteUser, useSendResetLink, useUsers } from '../../services/httpServices/queries';
import type { User } from '../../services/httpServices/userService';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { UserDetailsModal } from '../../components/ui/UserDetailsModal';
import { usePermissions } from '../../hooks/usePermissions';
import { getUserEditPath } from '../../utils/user';
import { cn } from '../../lib/utils';

function getRoleLabel(role: string) {
  return role
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * User management list.
 *
 * Staff user accounts are created from the Employees / Doctors screens; this
 * screen lets an administrator find a user, review their system and custom
 * roles, edit them, reassign roles, resend a password-reset link or delete the
 * account.
 */
export function Users() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pendingDelete, setPendingDelete] = useState<User | null>(null);
  const [resettingId, setResettingId] = useState<string | null>(null);
  const [viewUserId, setViewUserId] = useState<string | null>(null);

  const { hasPermission, hasAccess } = usePermissions();
  const canUpdateUser = hasPermission('users:update');
  const canDeleteUser = hasPermission('users:delete');

  const { data, isLoading, isFetching, error } = useUsers(page, 20, debouncedSearch || undefined);
  const deleteUser = useDeleteUser();
  const sendResetLink = useSendResetLink();

  const users = useMemo(() => data?.data ?? [], [data]);
  const meta = data?.meta;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteUser.mutateAsync(pendingDelete.id);
      toast.success('User deleted');
      setPendingDelete(null);
      if (users.length === 1 && page > 1) setPage((p) => p - 1);
    } catch {
      toast.error('Failed to delete user');
    }
  };

  const handleSendReset = async (user: User) => {
    setResettingId(user.id);
    try {
      await sendResetLink.mutateAsync(user.id);
      toast.success(`Password reset link sent to ${user.email || user.username}`);
    } catch {
      toast.error('Failed to send reset link');
    } finally {
      setResettingId(null);
    }
  };

  const isInitialLoading = isLoading && users.length === 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
            <UsersIcon className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text">Users</h1>
            <p className="text-sm text-text-muted">
              Manage staff accounts, their system roles and custom role assignments.
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/roles')}
          className="px-4 py-2 rounded-lg border border-border text-sm text-text hover:border-primary hover:text-primary transition-colors flex items-center gap-2"
        >
          <Shield size={16} />
          Manage Roles
        </button>
      </div>

      {/* Search */}
      <div className="bg-surface rounded-xl border border-border p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input
            type="text"
            placeholder="Search by name, username, email or mobile…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-9 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-danger"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {isInitialLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : error && users.length === 0 ? (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load users. Please try again.</span>
        </div>
      ) : users.length === 0 ? (
        <div className="bg-surface rounded-xl border border-dashed border-border py-16 text-center">
          <UsersIcon className="w-10 h-10 text-text-muted mx-auto mb-3" />
          <p className="text-text font-medium">
            {debouncedSearch ? 'No users match your search.' : 'No users found.'}
          </p>
          {debouncedSearch && (
            <button onClick={() => setSearch('')} className="mt-2 text-sm text-primary hover:underline">
              Clear search
            </button>
          )}
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          {isFetching && (
            <div className="flex items-center gap-2 px-4 py-2 border-b border-border text-xs text-text-muted">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Updating…
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Roles</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {users.map((user) => {
                  const initials = `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}` || user.username?.[0] || '?';
                  return (
                    <tr key={user.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm shrink-0">
                            {initials.toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <button
                              onClick={() => setViewUserId(user.id)}
                              className="text-sm font-medium text-text hover:text-primary text-left block truncate max-w-[200px]"
                            >
                              {[user.firstName, user.lastName].filter(Boolean).join(' ') || user.name || user.username}
                            </button>
                            <button
                              onClick={() => setViewUserId(user.id)}
                              className="text-xs text-text-muted hover:text-primary truncate max-w-[200px] block text-left"
                            >
                              @{user.username}
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-text truncate max-w-[220px]">{user.email || '—'}</p>
                        <p className="text-xs text-text-muted">{user.mobile || '—'}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1 max-w-[240px]">
                          {(user.roles || []).map((role) => (
                            <span
                              key={role}
                              className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary/10 text-primary"
                            >
                              {getRoleLabel(role)}
                            </span>
                          ))}
                          {(user.customRoles || []).map((role) => (
                            <span
                              key={role.id}
                              className={cn(
                                'px-1.5 py-0.5 rounded text-[10px] font-medium inline-flex items-center gap-1',
                                role.isWebsiteOnly
                                  ? 'bg-info/10 text-info'
                                  : 'bg-secondary/10 text-secondary',
                              )}
                              title={role.isWebsiteOnly ? 'Website-only role (blog author)' : 'Custom role'}
                            >
                              {role.isWebsiteOnly && <Globe size={9} />}
                              {role.name}
                            </span>
                          ))}
                          {(!user.roles || user.roles.length === 0) && (!user.customRoles || user.customRoles.length === 0) && (
                            <span className="text-xs text-text-muted">No roles</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium',
                            user.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger',
                          )}
                        >
                          {user.isActive ? <CheckCircle size={11} /> : <XCircle size={11} />}
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setViewUserId(user.id)}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                            title="View user"
                          >
                            <Eye size={16} />
                          </button>
                          {canUpdateUser && (
                            <button
                              onClick={() => navigate(getUserEditPath(user, hasAccess), { state: { from: '/users' } })}
                              className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                              title="Edit user"
                            >
                              <Pencil size={16} />
                            </button>
                          )}
                          {canUpdateUser && (
                            <button
                              onClick={() => navigate(`/users/${user.id}/roles`)}
                              className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-secondary transition-colors"
                              title="Manage roles"
                            >
                              <KeyRound size={16} />
                            </button>
                          )}
                          {canUpdateUser && (
                            <button
                              onClick={() => handleSendReset(user)}
                              disabled={resettingId === user.id}
                              className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-info transition-colors disabled:opacity-50"
                              title="Send password reset link"
                            >
                              {resettingId === user.id ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : (
                                <Send size={16} />
                              )}
                            </button>
                          )}
                          {canDeleteUser && (
                            <button
                              onClick={() => setPendingDelete(user)}
                              className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors"
                              title="Delete user"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={20}
            onPageChange={setPage}
            itemName="users"
          />
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title={`Delete user "${pendingDelete?.username ?? ''}"?`}
        description="This deactivates the account and removes access. This cannot be undone."
        confirmLabel="Delete user"
        loading={deleteUser.isPending}
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <UserDetailsModal
        userId={viewUserId}
        canUpdate={canUpdateUser}
        onClose={() => setViewUserId(null)}
        onManageRoles={
          canUpdateUser
            ? (user) => {
                setViewUserId(null);
                navigate(`/users/${user.id}/roles`);
              }
            : undefined
        }
        onEdit={
          canUpdateUser
            ? (user) => {
                setViewUserId(null);
                navigate(getUserEditPath(user, hasAccess), { state: { from: '/users' } });
              }
            : undefined
        }
      />
    </div>
  );
}
